import {
  computed,
  type ComputedRef,
  type InjectionKey,
  ref,
  type Ref,
} from 'vue';

export class BulkEdit<
  T extends Record<string, unknown> & { id: string } = { id: string },
> {
  private _items: Ref<Map<string, T>> = ref(new Map());
  private _selection = ref(new Set<string>());

  setItems(items: T[]) {
    this._selection.value.clear();
    this._items.value.clear();
    items.forEach((item) => {
      this._items.value.set(item.id, item);
    });
  }

  registerItem(item: T): void {
    this._items.value.set(item.id, item);
  }

  deregisterItem(id: string): void {
    this._items.value.delete(id);
    this._selection.value.delete(id);
  }

  items: ComputedRef<T[]> = computed(() =>
    Array.from(this._items.value.values()),
  );

  selectAll(): void {
    this.items.value.forEach((item) => this.select(item.id));
  }

  deselectAll(): void {
    this.items.value.forEach((item) => this.deselect(item.id));
  }

  select(id: string): void {
    this._selection.value.add(id);
  }

  deselect(id: string): void {
    this._selection.value.delete(id);
  }

  toggle(id: string): void {
    if (this._selection.value.has(id)) {
      this.deselect(id);
    } else {
      this.select(id);
    }
  }

  isSelected(id: string): ComputedRef<boolean> {
    return computed(() => this._selection.value.has(id));
  }

  selection: ComputedRef<T[]> = computed(() => {
    return Array.from(this._selection.value).map((key) => {
      const resolved = this._items.value.get(key);
      if (!resolved) {
        console.error(this._items.value);
        throw new Error(
          `Invalid Map Structure. Element with id ${key} is selected but not present in map`,
        );
      }
      return resolved;
    });
  });
}

export const bulkEditInjectionKey = Symbol(
  'BulkEdit',
) as InjectionKey<BulkEdit>;

export const bulkEditItemInjectionKey = Symbol('bulkEditItem') as InjectionKey<{
  id: string;
}>;
